import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, isApiError } from '@saay/api-client';
import { formatCount, formatTimestamp, statusLabel } from '@saay/formatting';
import {
  applyFieldErrors,
  Banner,
  Button,
  Checkbox,
  CommandFeedback,
  CountBadge,
  Dialog,
  EmptyState,
  ErrorState,
  FactList,
  FieldErrorList,
  LoadMore,
  NativeSelect,
  Skeleton,
  StatusChip,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import type {
  AdminMerchant,
  BranchRequest,
  BranchRequestDecision,
  BranchRequestRejection,
  MerchantBranch,
  MerchantBranchUpdate,
  MerchantRole,
  MerchantUserAssignment,
  MerchantUserUpdate,
} from '../api.js';
import {
  ADMIN_FORM_OPTIONS,
  mutationSubmitDisabled,
  useSubmitBlocked,
} from '../components/form.js';
import { FilterPill, FilterPillBar } from '../components/filter-pills.js';
import { SurfaceSheetSection } from '../components/surface-section.js';
import { QueryState } from '../components/guard.js';
import { GeographyFields } from '../components/pickers.js';
import { ReasonDialog } from '../components/reason-dialog.js';
import { canUse } from '../permissions.js';
import { listStatus, setupKeys, useBranchRequests } from '../queries.js';
import { reasonSchema } from '../validation.js';
import { isBranchRole, RoleChoiceField } from './merchant-dialogs.js';
import { useRefreshMerchant } from './merchant-lifecycle.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * Branches, branch requests, and staff on the merchant drawer (AC §18.4.6–§18.4.7; M4–M8; FTD §9.3).
 * Operations (and Super Admin) edit branches and staff and decide the merchant's branch requests; the
 * merchant is the maker and Operations the checker, so there is no approval request. Each change is
 * shown after the server's `200`.
 */

function useRefreshPeople(merchantId: string) {
  const queryClient = useQueryClient();
  const refresh = useRefreshMerchant(merchantId);
  return async (): Promise<void> => {
    await Promise.all([
      refresh(),
      queryClient.invalidateQueries({ queryKey: [...setupKeys.merchant(merchantId), 'branches'] }),
      queryClient.invalidateQueries({ queryKey: [...setupKeys.merchant(merchantId), 'users'] }),
      queryClient.invalidateQueries({
        queryKey: [...setupKeys.merchant(merchantId), 'branch-requests'],
      }),
    ]);
  };
}

// ---------------------------------------------------------------------------------------------
// M4 updateMerchantBranch

const branchSchema = z.object({
  addressFa: z.string().trim().min(1, 'آدرس الزامی است.').max(500, 'حداکثر ۵۰۰ کاراکتر.'),
  cityId: z.string().min(1, 'شهر را انتخاب کنید.'),
  nameFa: z.string().trim().min(1, 'نام شعبه الزامی است.').max(100, 'حداکثر ۱۰۰ کاراکتر.'),
  provinceId: z.string().min(1, 'استان را انتخاب کنید.'),
  reason: reasonSchema(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
});
type BranchValues = z.infer<typeof branchSchema>;

/** Only the changed fields (AC §18.4.6), with `expectedVersion` and the reason. */
export function toBranchUpdate(branch: MerchantBranch, values: BranchValues): MerchantBranchUpdate {
  return {
    expectedVersion: branch.version,
    reason: values.reason.trim(),
    ...(values.nameFa.trim() === branch.nameFa ? {} : { nameFa: values.nameFa.trim() }),
    ...(values.addressFa.trim() === branch.addressFa ? {} : { addressFa: values.addressFa.trim() }),
    ...(values.provinceId === branch.province.id && values.cityId === branch.city.id
      ? {}
      : { cityId: values.cityId, provinceId: values.provinceId }),
    ...(values.status === branch.status ? {} : { status: values.status }),
  };
}

export function EditBranchDialog({
  branch,
  merchant,
  onOpenChange,
}: {
  readonly branch: MerchantBranch;
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshPeople(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<BranchValues>({
    defaultValues: {
      addressFa: branch.addressFa,
      cityId: branch.city.id,
      nameFa: branch.nameFa,
      provinceId: branch.province.id,
      reason: '',
      status: branch.status,
    },
    resolver: zodResolver(branchSchema),
    ...ADMIN_FORM_OPTIONS,
  });
  const values = form.watch();
  const branchBody = toBranchUpdate(branch, values);
  const hasChanges = Object.keys(branchBody).some(
    (key) => key !== 'expectedVersion' && key !== 'reason',
  );
  const deactivating = values.status === 'INACTIVE' && branch.status === 'ACTIVE';
  const command = useCommand<MerchantBranchUpdate, MerchantBranch>(
    'merchant.branch.update',
    (client, body) =>
      client.patch(`/admin/merchant-branches/${encodeURIComponent(branch.id)}`, { body }),
    {
      onSuccess: async (result) => {
        await refresh();
        toast.success({ confirmedBy: result, title: `شعبه «${result.data.nameFa}» به‌روز شد` });
        onOpenChange(false);
      },
    },
  );

  async function handle(outcome: CommandOutcome<MerchantBranch>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(
      applyFieldErrors(
        outcome.error,
        ['nameFa', 'provinceId', 'cityId', 'addressFa', 'status', 'reason'],
        form.setError,
      ),
    );
    if (outcome.error.code === 'GEOGRAPHY_MISMATCH') {
      form.setError('cityId', { message: outcome.error.messageFa, type: 'server' });
    }
    if (outcome.error.code === 'RESOURCE_VERSION_CONFLICT') await refresh();
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const lastBranch = isApiError(error) && error.code === 'LAST_ACTIVE_BRANCH';

  return (
    <Dialog
      eyebrow={merchant.displayNameFa}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title={`ویرایش شعبه «${branch.nameFa}»`}
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          const body = toBranchUpdate(branch, values);
          if (Object.keys(body).every((key) => key === 'expectedVersion' || key === 'reason')) {
            return;
          }
          await handle(await command.submit(body));
        })}
      >
        <TextField
          error={form.formState.errors.nameFa?.message}
          label="نام شعبه"
          {...form.register('nameFa')}
        />
        <Controller
          control={form.control}
          name="provinceId"
          render={({ field: province }) => (
            <Controller
              control={form.control}
              name="cityId"
              render={({ field: city }) => (
                <GeographyFields
                  cityError={form.formState.errors.cityId?.message}
                  cityId={city.value}
                  idPrefix={`branch-${branch.id}`}
                  onCityChange={city.onChange}
                  onProvinceChange={(value) => {
                    province.onChange(value);
                    city.onChange('');
                  }}
                  provinceError={form.formState.errors.provinceId?.message}
                  provinceId={province.value}
                />
              )}
            />
          )}
        />
        <TextArea
          error={form.formState.errors.addressFa?.message}
          label="آدرس"
          {...form.register('addressFa')}
        />
        <NativeSelect
          label="وضعیت"
          options={[
            { label: statusLabel('activeStatus', 'ACTIVE'), value: 'ACTIVE' },
            { label: statusLabel('activeStatus', 'INACTIVE'), value: 'INACTIVE' },
          ]}
          {...form.register('status')}
        />
        {deactivating ? (
          <Banner tone="warning">
            با غیرفعال‌سازی، شعبه از دسترسی کارکنان برداشته می‌شود؛ فروشنده یا فروشنده سیاری که
            شعبهٔ دیگری نداشته باشد غیرفعال می‌شود.
          </Banner>
        ) : null}
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل تغییر"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="merchant." />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        {lastBranch ? (
          <Banner tone="warning">
            این آخرین شعبهٔ فعال فروشگاه است؛ فروشگاه فعال دست‌کم یک شعبهٔ فعال لازم دارد.
          </Banner>
        ) : null}
        <div className="admin-actions admin-actions--end">
          <Button
            disabled={mutationSubmitDisabled(
              offline,
              hasChanges,
              command.state.status === 'pending',
            )}
            loading={command.state.status === 'pending'}
            type="submit"
          >
            ذخیره شعبه
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// M5–M7 branch requests

export function proposedText(request: BranchRequest): string {
  const { proposed } = request;
  const parts = [
    proposed.nameFa,
    proposed.province === null || proposed.city === null
      ? null
      : `${proposed.province.nameFa} / ${proposed.city.nameFa}`,
    proposed.addressFa,
  ].filter((part): part is string => part !== null && part !== '');
  return parts.length === 0 ? '—' : parts.join(' · ');
}

function decisionGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  if (error.code === 'BRANCH_REQUEST_ALREADY_DECIDED') {
    const status = error.details?.status;
    return typeof status === 'string'
      ? `این درخواست قبلاً «${statusLabel('branchRequestStatus', status)}» شده است.`
      : 'این درخواست قبلاً بررسی شده است.';
  }
  if (error.code === 'LAST_ACTIVE_BRANCH') {
    return 'این آخرین شعبهٔ فعال فروشگاه است و غیرفعال نمی‌شود.';
  }
  if (error.code === 'RESOURCE_VERSION_CONFLICT') {
    return 'درخواست در این فاصله تغییر کرده است؛ فهرست به‌روز شد.';
  }
  return undefined;
}

type Decision = { readonly kind: 'approve' | 'reject'; readonly request: BranchRequest };

export function BranchRequestsSection({
  merchant,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly self: AdminSelf;
}) {
  const toast = useToast();
  const refresh = useRefreshPeople(merchant.id);
  const [filter, setFilter] = useState<'PENDING' | 'ALL'>('PENDING');
  const requests = useBranchRequests(merchant.id, filter === 'PENDING' ? 'PENDING' : undefined);
  const rows = requests.data?.pages.flatMap((page) => page.items) ?? [];
  const [decision, setDecision] = useState<Decision | null>(null);
  const canDecide =
    merchant.status !== 'CLOSED' &&
    canUse(self, 'approveBranchRequest') &&
    canUse(self, 'rejectBranchRequest');
  if (!canUse(self, 'listAdminBranchRequests')) {
    return null;
  }
  if (
    merchant.pendingBranchRequestCount === 0 &&
    filter === 'PENDING' &&
    listStatus(requests) === 'success' &&
    rows.length === 0
  ) {
    return null;
  }
  const close = (open: boolean) => {
    if (!open) setDecision(null);
  };
  return (
    <SurfaceSheetSection
      id="merchant-branch-requests"
      title={
        <>
          درخواست‌های شعبه{' '}
          {merchant.pendingBranchRequestCount === 0 ? null : (
            <CountBadge tone="warning">{`${formatCount(merchant.pendingBranchRequestCount)} در انتظار`}</CountBadge>
          )}
        </>
      }
    >
      <FilterPillBar
        activeFilterCount={filter === 'PENDING' ? 0 : 1}
        label="فیلتر درخواست‌های شعبه"
        onClearAll={() => setFilter('PENDING')}
      >
        <FilterPill
          emptyLabel="در انتظار"
          label="وضعیت درخواست"
          onChange={(value) => setFilter(value === 'ALL' ? 'ALL' : 'PENDING')}
          options={[{ label: 'همه درخواست‌ها', value: 'ALL' }]}
          value={filter === 'PENDING' ? '' : filter}
        />
      </FilterPillBar>
      <QueryState
        empty={
          <EmptyState
            icon="store"
            title={filter === 'PENDING' ? 'درخواستی در انتظار بررسی نیست' : 'درخواستی ثبت نشده است'}
          />
        }
        error={requests.error}
        isEmpty={rows.length === 0}
        onRetry={() => void requests.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری درخواست‌ها…" lines={3} />}
        status={listStatus(requests)}
      >
        <div aria-label="درخواست‌های شعبه" className="admin-branch-request-cards" role="list">
          {rows.map((row) => (
            <article className="admin-branch-request-card" key={row.id} role="listitem">
              <div className="admin-branch-request-card__head">
                <strong>{statusLabel('branchRequestType', row.requestType)}</strong>
                <StatusChip enumName="branchRequestStatus" value={row.status} />
              </div>
              <p className="admin-muted">{proposedText(row)}</p>
              <p className="admin-muted">{row.reason}</p>
              <p className="admin-muted">
                {row.requestedBy.displayNameFa ?? 'بدون نام'}
                {' · '}
                <time dateTime={row.requestedAt}>{formatTimestamp(row.requestedAt)}</time>
              </p>
              {row.status !== 'PENDING' || !canDecide ? null : (
                <div className="admin-actions">
                  <Button onClick={() => setDecision({ kind: 'approve', request: row })} size="sm">
                    اعمال
                  </Button>
                  <Button
                    onClick={() => setDecision({ kind: 'reject', request: row })}
                    size="sm"
                    variant="ghost"
                  >
                    رد
                  </Button>
                </div>
              )}
            </article>
          ))}
        </div>
        {requests.isFetchNextPageError ? (
          <ErrorState error={requests.error} onRetry={() => void requests.fetchNextPage()} />
        ) : null}
        <LoadMore
          hasMore={requests.hasNextPage}
          loading={requests.isFetchingNextPage}
          onLoadMore={() => void requests.fetchNextPage()}
        />
      </QueryState>
      {decision === null ? null : (
        <ReasonDialog<BranchRequest>
          description={
            decision.kind === 'approve'
              ? 'درخواست با همین مقادیر روی شعب فروشگاه اعمال می‌شود.'
              : 'درخواست رد می‌شود و تغییری در شعب ایجاد نمی‌شود.'
          }
          eyebrow={statusLabel('branchRequestType', decision.request.requestType)}
          guidance={decisionGuidance}
          name={`branch-request.${decision.kind}`}
          onDone={async (result) => {
            await refresh();
            toast.success({
              confirmedBy: result,
              title: decision.kind === 'approve' ? 'درخواست شعبه اعمال شد' : 'درخواست شعبه رد شد',
            });
            setDecision(null);
          }}
          onError={async (error) => {
            if (
              isApiError(error) &&
              (error.code === 'BRANCH_REQUEST_ALREADY_DECIDED' ||
                error.code === 'RESOURCE_VERSION_CONFLICT')
            ) {
              await refresh();
            }
          }}
          onOpenChange={close}
          reasonLabel={decision.kind === 'approve' ? 'توضیح' : 'دلیل رد'}
          required={decision.kind === 'reject'}
          send={(client, reason, intent) =>
            client.post(
              `/admin/branch-requests/${encodeURIComponent(decision.request.id)}/${decision.kind === 'approve' ? 'approval' : 'rejection'}`,
              {
                body:
                  decision.kind === 'approve'
                    ? ({
                        expectedVersion: decision.request.version,
                        ...(reason === '' ? {} : { reason }),
                      } satisfies BranchRequestDecision)
                    : ({
                        expectedVersion: decision.request.version,
                        reason,
                      } satisfies BranchRequestRejection),
                intent,
              },
            )
          }
          submitLabel={decision.kind === 'approve' ? 'اعمال درخواست' : 'رد درخواست'}
          title={decision.kind === 'approve' ? 'اعمال درخواست شعبه' : 'رد درخواست شعبه'}
          variant={decision.kind === 'approve' ? 'primary' : 'danger'}
        >
          <FactList
            items={[
              {
                key: 'type',
                label: 'نوع',
                value: statusLabel('branchRequestType', decision.request.requestType),
              },
              { key: 'proposed', label: 'پیشنهاد', value: proposedText(decision.request) },
              { key: 'reason', label: 'دلیل فروشگاه', value: decision.request.reason },
            ]}
          />
        </ReasonDialog>
      )}
    </SurfaceSheetSection>
  );
}

// ---------------------------------------------------------------------------------------------
// M8 updateAdminMerchantUser

const staffSchema = z
  .object({
    branchIds: z.array(z.string()),
    disable: z.boolean(),
    reason: reasonSchema(),
    role: z.enum(['MERCHANT_ADMIN', 'FINANCE_MANAGER', 'SELLER', 'COURIER']),
  })
  .superRefine((value, context) => {
    if (!value.disable && isBranchRole(value.role) && value.branchIds.length === 0) {
      context.addIssue({
        code: 'custom',
        message: 'برای این نقش دست‌کم یک شعبه انتخاب کنید.',
        path: ['branchIds'],
      });
    }
  });
type StaffValues = z.infer<typeof staffSchema>;

function sameSet(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((item) => right.includes(item));
}

/** Only the changed fields (AC §18.4.7); disabling is terminal and sent alone with the reason. */
export function toStaffUpdate(
  user: MerchantUserAssignment,
  values: StaffValues,
): MerchantUserUpdate {
  if (values.disable) {
    return { expectedVersion: user.version, reason: values.reason.trim(), status: 'DISABLED' };
  }
  const branchIds = isBranchRole(values.role) ? values.branchIds : [];
  const current = user.branches.map((branch) => branch.id);
  return {
    expectedVersion: user.version,
    reason: values.reason.trim(),
    ...(values.role === user.role ? {} : { role: values.role }),
    ...(sameSet(branchIds, current) && values.role === user.role ? {} : { branchIds }),
  };
}

export function EditStaffDialog({
  activeBranches,
  merchant,
  onOpenChange,
  user,
}: {
  readonly activeBranches: readonly MerchantBranch[];
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
  readonly user: MerchantUserAssignment;
}) {
  const toast = useToast();
  const refresh = useRefreshPeople(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<StaffValues>({
    defaultValues: {
      branchIds: user.branches.map((branch) => branch.id),
      disable: false,
      reason: '',
      role: user.role,
    },
    resolver: zodResolver(staffSchema),
    ...ADMIN_FORM_OPTIONS,
  });
  const values = form.watch();
  const staffBody = toStaffUpdate(user, values);
  const hasChanges = Object.keys(staffBody).some(
    (key) => key !== 'expectedVersion' && key !== 'reason',
  );
  const role = values.role as MerchantRole;
  const disable = values.disable;
  const command = useCommand<MerchantUserUpdate, MerchantUserAssignment>(
    'merchant.user.update',
    (client, body) =>
      client.patch(`/admin/merchant-users/${encodeURIComponent(user.assignmentId)}`, { body }),
    {
      onSuccess: async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          title:
            result.data.status === 'DISABLED'
              ? 'دسترسی کاربر غیرفعال شد'
              : 'دسترسی کاربر به‌روز شد',
        });
        onOpenChange(false);
      },
    },
  );

  async function handle(outcome: CommandOutcome<MerchantUserAssignment>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const rest = applyFieldErrors(outcome.error, ['role', 'reason', 'status'], form.setError);
    const branchError = rest.find((item) => item.field.startsWith('branchIds'));
    if (branchError !== undefined) {
      form.setError('branchIds', { message: branchError.messageFa, type: 'server' });
    }
    setUnmapped(rest.filter((item) => !item.field.startsWith('branchIds')));
    if (
      outcome.error.code === 'RESOURCE_VERSION_CONFLICT' ||
      outcome.error.code === 'STATE_CONFLICT'
    ) {
      await refresh();
    }
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const lastAdmin = isApiError(error) && error.code === 'LAST_MERCHANT_ADMIN';

  return (
    <Dialog
      eyebrow={user.user.displayNameFa ?? 'کاربر فروشگاه'}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="ویرایش دسترسی کاربر"
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          const body = toStaffUpdate(user, values);
          if (Object.keys(body).every((key) => key === 'expectedVersion' || key === 'reason')) {
            return;
          }
          await handle(await command.submit(body));
        })}
      >
        <Controller
          control={form.control}
          name="role"
          render={({ field, fieldState }) => (
            <RoleChoiceField
              disabled={disable}
              error={fieldState.error?.message}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        {isBranchRole(role) && !disable ? (
          <fieldset className="admin-fieldset">
            <legend>شعبه‌های مجاز</legend>
            <Controller
              control={form.control}
              name="branchIds"
              render={({ field }) => (
                <div className="admin-checkbox-list">
                  {activeBranches.map((branch) => (
                    <Checkbox
                      checked={field.value.includes(branch.id)}
                      key={branch.id}
                      label={branch.nameFa}
                      onChange={(event) =>
                        field.onChange(
                          event.target.checked
                            ? [...field.value, branch.id]
                            : field.value.filter((id) => id !== branch.id),
                        )
                      }
                    />
                  ))}
                </div>
              )}
            />
            {form.formState.errors.branchIds?.message === undefined ? null : (
              <span className="saay-field__error" role="alert">
                {form.formState.errors.branchIds.message}
              </span>
            )}
          </fieldset>
        ) : null}
        <Controller
          control={form.control}
          name="disable"
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              hint="غیرفعال‌سازی قابل برگشت نیست و کاربر را از همهٔ دستگاه‌ها خارج می‌کند؛ برای دسترسی دوباره، کاربر دوباره دعوت می‌شود."
              label="غیرفعال‌سازی دسترسی"
              onChange={(event) => field.onChange(event.target.checked)}
            />
          )}
        />
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل تغییر"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="merchant." />
        <FieldErrorList errors={unmapped} />
        <CommandFeedback
          onCheckStatus={() => void command.checkStatus().then(handle)}
          state={command.state}
        />
        {lastAdmin ? (
          <Banner tone="warning">
            این آخرین مدیر فعال فروشگاه است؛ پیش از غیرفعال‌سازی یا تغییر نقش، مدیر دیگری اضافه
            کنید.
          </Banner>
        ) : null}
        <div className="admin-actions admin-actions--end">
          <Button
            disabled={mutationSubmitDisabled(
              offline,
              hasChanges,
              command.state.status === 'pending',
            )}
            loading={command.state.status === 'pending'}
            type="submit"
            variant={disable ? 'danger' : 'primary'}
          >
            {disable ? 'غیرفعال‌سازی' : 'ذخیره دسترسی'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
